<template>
    <div class="content">
        <h3 class="mt-2 mb-5 ml-2 is-hidden-desktop"><i class="fal fa-credit-card fa-fw mr-3"></i>Payment Method</h3>

        <!-- Loading -->
        <div class="content mt-6 mb-6" v-if="loading">
            <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Loading your payment methods.  Please wait...</h5>
        </div>

        <!-- No payment plan on this registration -->
        <div class="content mt-6 mb-6" v-else-if="noPlan">
            <h5 class="has-text-grey"><i class="fal fa-info-circle mr-3"></i>There is no payment plan on this trip yet, so there are no cards to manage.  Please check back later.</h5>
        </div>

        <div v-else>
            <!-- Existing cards -->
            <div class="white-container content">
                <div class="columns is-mobile">
                    <div class="column is-narrow pr-0"><i class="fal fa-wallet fa-fw is-size-4"></i></div>
                    <div class="column">
                        <h4>Card on File</h4>

                        <p class="is-size-7 has-text-grey mb-4">The Tennis Ventures team uses this card to run the scheduled installments on this trip's payment plan.  You can keep one card on file at a time &mdash; adding a new card replaces the current one.</p>

                        <p class="has-text-grey" v-if="!methods.length">You don't have a card on file for this trip yet.  Add one below.</p>

                        <div v-for="m in methods" :key="m.id">
                            <div class="columns is-mobile is-vcentered">
                                <div class="column">
                                    <h6 class="mb-1"><i class="fal fa-credit-card mr-2"></i>Card ending in {{ m.last }}</h6>
                                    <div class="is-size-7 has-text-grey">Expires {{ formatExp(m.expiration) }}</div>
                                </div>
                                <div class="column has-text-right is-narrow">
                                    <b-button size="is-small" type="is-danger" outlined
                                              :disabled="processing"
                                              @click="remove(m)">
                                        <i class="fas fa-trash mr-1"></i>Remove
                                    </b-button>
                                </div>
                            </div>
                            <hr class="mt-0 mb-3"/>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add a card -->
            <div class="white-container content">
                <div class="columns is-mobile">
                    <div class="column is-narrow pr-0"><i class="fal fa-plus-circle fa-fw is-size-4"></i></div>
                    <div class="column">
                        <h4 class="mb-4">{{ hasCard ? 'Update Your Card' : 'Add a Card' }}</h4>

                        <p class="is-size-7 has-text-grey mb-4" v-if="hasCard">Saving a new card will replace the card ending in {{ methods[0].last }}.</p>

                        <b-message type="is-danger" v-if="error" has-icon>{{ error }}</b-message>

                        <div class="columns">
                            <div class="column">
                                <b-field label="Card Number" label-position="inside">
                                    <b-input v-model="card.number" placeholder="•••• •••• •••• ••••"
                                             inputmode="numeric" maxlength="19" :has-counter="false"/>
                                </b-field>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <b-field label="Exp. Month" label-position="inside">
                                    <b-select v-model="card.month" expanded>
                                        <option v-for="mo in months" :key="mo" :value="mo">{{ mo }}</option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Exp. Year" label-position="inside">
                                    <b-select v-model="card.year" expanded>
                                        <option v-for="yr in years" :key="yr" :value="yr">{{ yr }}</option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Security Code (CVV)" label-position="inside">
                                    <b-input v-model="card.code" placeholder="•••"
                                             inputmode="numeric" maxlength="4" :has-counter="false"/>
                                </b-field>
                            </div>
                        </div>

                        <div class="has-text-right mt-4">
                            <b-button type="is-primary" :loading="processing" :disabled="processing || !acceptReady"
                                      @click="add">
                                <i class="fas fa-lock mr-2"></i>{{ hasCard ? 'Replace Card' : 'Save Card' }}
                            </b-button>
                        </div>
                        <p class="is-size-7 has-text-grey has-text-right mt-2">
                            <i class="fal fa-shield-check mr-1"></i>Your card is encrypted in your browser and sent directly to our payment processor.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import {client as http, axiosInstance} from "../../http_client";

    export default {
        data: function() {
            return {
                methods: [],
                loading: true,
                noPlan: false,
                processing: false,
                error: null,
                acceptReady: false,
                anet: {clientKey: '', apiLoginID: ''},
                card: {number: '', month: '', year: '', code: ''}
            };
        },
        computed: {
            ...mapGetters(['registration']),
            regId: function() {
                return this.registration && this.registration.id;
            },
            // Single card on file: the backend keeps exactly one card per plan, so
            // this drives the "Add" vs "Update/Replace" wording.
            hasCard: function() {
                return this.methods.length > 0;
            },
            months: function() {
                return Array.from({length: 12}, (_, i) => String(i + 1).padStart(2, '0'));
            },
            years: function() {
                // Authorize.net stores/returns a 4-digit year; offer the next 15.
                const start = new Date().getFullYear();
                return Array.from({length: 16}, (_, i) => String(start + i));
            }
        },
        methods: {
            // Turn the stored MMYYYY string into MM/YY for display.
            formatExp: function(exp) {
                if (!exp) return '';
                const s = String(exp);
                return s.substr(0, 2) + '/' + s.substr(4, 2);
            },
            // Load Accept.js publishable keys and inject the Accept.js script.
            loadConfig: function() {
                return axiosInstance.get('/app/registrations/payment-config').then(res => {
                    const anet = res.data || {};
                    this.anet.clientKey = anet.clientKey || '';
                    this.anet.apiLoginID = anet.loginId || '';
                    if (window.Accept) {
                        this.acceptReady = true;
                    } else if (anet.acceptJsUrl) {
                        const s = document.createElement('script');
                        s.src = anet.acceptJsUrl;
                        s.onload = () => { this.acceptReady = true; };
                        document.head.appendChild(s);
                    }
                }).catch(() => {
                    // Non-fatal: the button stays disabled and we surface it on submit.
                });
            },
            loadMethods: function() {
                if (!this.regId) return;
                this.loading = true;
                this.noPlan = false;
                http.get('/app/registrations/' + this.regId + '/payment-methods', {force: true})
                    .then(response => {
                        const data = response.data || {};
                        // The API returns { plan, methods }; a null plan means this
                        // registration has no payment plan to attach a card to yet.
                        this.noPlan = !data.plan;
                        this.methods = data.methods || [];
                        this.loading = false;
                    })
                    .catch(() => {
                        this.methods = [];
                        this.loading = false;
                    });
            },
            add: function() {
                this.error = null;

                const digits = String(this.card.number).replace(/\D/g, '');
                if (digits.length < 13) {
                    this.error = 'Please enter a valid card number.';
                    return;
                }
                if (!this.card.month || !this.card.year) {
                    this.error = 'Please choose the card expiration date.';
                    return;
                }
                if (String(this.card.code).replace(/\D/g, '').length < 3) {
                    this.error = 'Please enter the security code (CVV) from your card.';
                    return;
                }
                if (!window.Accept) {
                    this.error = 'The secure payment library is still loading. Please try again in a moment.';
                    return;
                }

                this.processing = true;
                const self = this;

                const secureData = {
                    authData: {clientKey: this.anet.clientKey, apiLoginID: this.anet.apiLoginID},
                    cardData: {
                        cardNumber: digits,
                        month: this.card.month,
                        year: this.card.year,
                        cardCode: String(this.card.code).replace(/\D/g, '')
                    }
                };

                // Accept.js can briefly return E_WC_14 ("encryption failed") if it's
                // still initializing — retry a few times before surfacing an error.
                let attempts = 0;
                const dispatch = () => {
                    window.Accept.dispatchData(secureData, function(response) {
                        if (response.messages.resultCode === 'Error') {
                            const codes = response.messages.message.map(m => m.code);
                            if (codes.indexOf('E_WC_14') !== -1 && attempts < 5) {
                                attempts++;
                                setTimeout(dispatch, 500);
                                return;
                            }
                            self.processing = false;
                            self.error = response.messages.message.map(m => m.text).join(' ');
                            return;
                        }
                        self.save(response.opaqueData, digits);
                    });
                };
                dispatch();
            },
            save: function(opaqueData, digits) {
                const payload = {
                    opaqueData: {dataDescriptor: opaqueData.dataDescriptor, dataValue: opaqueData.dataValue},
                    cardMeta: {last4: digits.slice(-4), expiration: this.card.month + this.card.year}
                };

                const wasReplacing = this.hasCard;
                axiosInstance.post('/app/registrations/' + this.regId + '/payment-methods', payload)
                    .then(() => {
                        this.processing = false;
                        this.card = {number: '', month: '', year: '', code: ''};
                        this.$notify({
                            group: 'notify',
                            type: 'success',
                            title: wasReplacing ? 'Card Updated' : 'Card Saved',
                            text: wasReplacing
                                ? 'Your new card is on file and the previous card has been removed.'
                                : 'Your card has been saved securely.'
                        });
                        this.loadMethods();
                    })
                    .catch(err => {
                        this.processing = false;
                        const msg = (err.response && err.response.data && err.response.data.message)
                            ? err.response.data.message
                            : 'We could not save that card. Please check the details and try again.';
                        this.error = msg;
                    });
            },
            remove: function(method) {
                this.$buefy.dialog.confirm({
                    title: 'Remove Card',
                    message: 'Remove the card ending in <strong>' + method.last + '</strong>?',
                    confirmText: 'Remove Card',
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () => {
                        this.processing = true;
                        axiosInstance.delete('/app/registrations/' + this.regId + '/payment-methods/' + method.id)
                            .then(() => {
                                this.processing = false;
                                this.$notify({
                                    group: 'notify',
                                    type: 'success',
                                    title: 'Card Removed',
                                    text: 'Your card has been removed.'
                                });
                                this.loadMethods();
                            })
                            .catch(() => {
                                this.processing = false;
                                this.$notify({
                                    group: 'notify',
                                    type: 'error',
                                    title: 'Could Not Remove Card',
                                    text: 'Something went wrong. Please try again.'
                                });
                            });
                    }
                });
            }
        },
        watch: {
            registration: function() {
                this.loadMethods();
            }
        },
        mounted() {
            this.loadConfig();
            this.loadMethods();
        }
    };
</script>

<style lang="scss" scoped>

</style>
